export class Leaderboard {
    constructor(gameServices = null) {
        this.scores = this.loadScores();
        this.apiUrl = this.getApiUrl();
        this.syncInProgress = false;
        this.gameServices = gameServices; // GameServices instance (optional)
    }

    getApiUrl() {
        // Check if API URL is configured
        if (typeof process !== 'undefined' && process.env.API_BASE_URL) {
            return process.env.API_BASE_URL;
        }
        // Default to localhost for development, or null for local-only mode
        return window.location.hostname === 'localhost' ? 'http://localhost:8000' : null;
    }

    loadScores() {
        const saved = localStorage.getItem('leaderboard');
        if (saved) {
            return JSON.parse(saved);
        }
        return [];
    }

    saveScores() {
        localStorage.setItem('leaderboard', JSON.stringify(this.scores));
    }

    async syncWithBackend(playerName, score, kills, level = 1) {
        if (!this.apiUrl || this.syncInProgress) return;

        this.syncInProgress = true;
        try {
            const response = await fetch(`${this.apiUrl}/scores/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    username: playerName,
                    score: score,
                    kills: kills,
                    level: level
                })
            });

            if (response.ok) {
                console.log('Score synced to backend');
                // Optionally reload leaderboard from backend
                await this.loadFromBackend();
            }
        } catch (error) {
            console.warn('Failed to sync score to backend:', error);
        } finally {
            this.syncInProgress = false;
        }
    }

    async loadFromBackend() {
        if (!this.apiUrl) return;

        try {
            const response = await fetch(`${this.apiUrl}/leaderboard/top10`);
            if (response.ok) {
                const backendScores = await response.json();

                // Merge backend scores with local scores
                const mergedScores = [...backendScores, ...this.scores];

                // Convert backend format to local format
                this.scores = mergedScores.map(s => ({
                    name: s.username || s.name,
                    score: s.score,
                    kills: s.kills,
                    date: s.created_at || s.date,
                    id: s.id || Date.now() + Math.random()
                }));

                // Remove duplicates and sort
                const uniqueScores = [];
                const seen = new Set();

                this.scores.forEach(score => {
                    const key = `${score.name}-${score.score}`;
                    if (!seen.has(key)) {
                        seen.add(key);
                        uniqueScores.push(score);
                    }
                });

                this.scores = uniqueScores;
                this.scores.sort((a, b) => b.score - a.score);
                this.scores = this.scores.slice(0, 100);

                this.saveScores();
            }
        } catch (error) {
            console.warn('Failed to load leaderboard from backend:', error);
        }
    }

    addScore(playerName, score, kills, level = 1) {
        const entry = {
            name: playerName,
            score: score,
            kills: kills,
            date: new Date().toISOString(),
            id: Date.now() + Math.random()
        };

        this.scores.push(entry);
        this.scores.sort((a, b) => b.score - a.score);
        this.scores = this.scores.slice(0, 100); // Keep top 100
        this.saveScores();

        // Sync to backend asynchronously
        this.syncWithBackend(playerName, score, kills, level);

        // Submit to Google Play Games / Game Center
        if (this.gameServices?.isAvailable()) {
            this.gameServices.submitScore(score);
        }

        return this.getRank(entry.id);
    }

    getRank(entryId) {
        return this.scores.findIndex(s => s.id === entryId) + 1;
    }

    getTopScores(count = 10) {
        return this.scores.slice(0, count);
    }

    getPlayerBest(playerName) {
        const playerScores = this.scores.filter(s => s.name === playerName);
        if (playerScores.length > 0) {
            return playerScores[0];
        }
        return null;
    }

    // Show the native platform leaderboard (Game Center / Google Play Games).
    // Returns true when the native UI was opened, false when falling back to local.
    async showNative() {
        if (this.gameServices?.isAvailable()) {
            return this.gameServices.showLeaderboard();
        }
        return false;
    }

    clear() {
        this.scores = [];
        this.saveScores();
    }
}
